import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { OrdersApiService } from '../../../services/orders-api.service';
import { GiftAttachment, Order } from '../../../models/order';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../../../shared/services/translations/language.service';
import { ApiErrorService } from '../../../../../shared/services/api-error.service';
import { ToastNotificationService } from '../../../../../notifications/services/toast-notification.service';
import { ToastNotificationType, ToastPosition } from '../../../../../notifications/models/toast-notification-type';

@Component({
  selector: 'app-order-details-page',
  imports: [CommonModule, RouterLink, TranslateModule],
  templateUrl: './order-details-page.component.html',
  styleUrl: './order-details-page.component.css'
})
export class OrderDetailsPageComponent implements OnInit, OnDestroy {

  route = inject(ActivatedRoute);
  apiService = inject(OrdersApiService);
  apiErrorService = inject(ApiErrorService);
  toast = inject(ToastNotificationService);
  translate = inject(TranslateService);
  languageService = inject(LanguageService);

  orderUUID = this.route.snapshot.paramMap.get('id');

  isUpdateStatus = false;
  isRejecting = false;

  order: Order | undefined;

  /** Attachment currently shown in the preview popup, or null when it's closed. */
  previewAttachment: GiftAttachment | null = null;

  /** Id of the attachment whose text was just copied — drives the brief "copied"
   *  icon swap on the copy buttons. Reset to null by {@link copyResetTimer}. */
  copiedAttachmentId: number | null = null;
  private copyResetTimer?: ReturnType<typeof setTimeout>;

  /** Sum of all line totals. Computed once when the order loads (see
   *  {@link recomputeOrderDerivedData}) rather than in a getter, so the template
   *  reads a stable value instead of recomputing on every change-detection pass. */
  totalOrderPrice = 0;

  /** Every item's customization media, kept grouped by media-set: one entry per
   *  group of `quantity` units that share the same set of `attachments` (a single
   *  unit can carry several media). Computed once on load and held as a field —
   *  NOT a getter. A getter returned a brand-new array of brand-new objects on
   *  every change-detection pass, so the `*ngFor` below saw all-new identities each
   *  cycle and tore down + rebuilt the whole media subtree (and its impure
   *  `| translate` pipes) every time, which froze the tab on orders that carry gift
   *  media. A stable reference lets `*ngFor` no-op between cycles. */
  customizationGroups: { productName: string; quantity: number; attachments: GiftAttachment[] }[] = [];

  get whatsappText(): string {
    return encodeURIComponent(
      this.translate.instant('orders.details.whatsapp_message', { uuid: this.orderUUID }),
    );
  }

  /** Populate the cached, template-facing derived data for a freshly loaded order. */
  private recomputeOrderDerivedData(order: Order): void {
    this.totalOrderPrice = order.items.reduce((total, item) => total + item.price * item.quantity, 0);

    const groups: { productName: string; quantity: number; attachments: GiftAttachment[] }[] = [];
    for (const item of order.items) {
      const productName = item.product_name || item.variant?.name || '';
      for (const group of item.groups ?? []) {
        const attachments = group.attachments ?? [];
        if (attachments.length)
          groups.push({ productName, quantity: group.quantity, attachments });
      }
    }
    this.customizationGroups = groups;
  }

  trackByIndex(index: number): number {
    return index;
  }

  mediaIcon(type: string): string {
    switch (type) {
      case 'image': return 'bi-image';
      case 'video': return 'bi-camera-video';
      case 'audio': return 'bi-mic';
      case 'text': return 'bi-fonts';
      default: return 'bi-paperclip';
    }
  }

  openAttachmentPreview(attachment: GiftAttachment): void {
    this.previewAttachment = attachment;
  }

  closeAttachmentPreview(): void {
    this.previewAttachment = null;
  }

  copyAttachmentText(attachment: GiftAttachment): void {
    if (!attachment.text_content)
      return;

    navigator.clipboard?.writeText(attachment.text_content);
    this.copiedAttachmentId = attachment.id;
    clearTimeout(this.copyResetTimer);
    this.copyResetTimer = setTimeout(() => (this.copiedAttachmentId = null), 1500);
  }

  ngOnDestroy(): void {
    clearTimeout(this.copyResetTimer);
  }

  downloadAttachment(attachment: GiftAttachment): void {
    if (!attachment.file)
      return;

    const link = document.createElement('a');
    link.href = attachment.file;
    link.target = '_blank';
    link.download = attachment.file.split('/').pop()?.split('?')[0] ?? 'attachment';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  ngOnInit(): void {

    if (!this.orderUUID) {
      alert(this.translate.instant('orders.errors.uuid_missing'));
      return;
    }

    this.apiService.getOrderByUUID(this.orderUUID).subscribe({
      next: order => {
        this.order = order;
        this.recomputeOrderDerivedData(order);
        // this.order.status = 'placed';
      },
      error: error => {
        this.toast.createNotification(this.apiErrorService.getDisplayMessage(error, this.translate.instant('orders.errors.fetch_failed')), ToastNotificationType.ERROR, '', ToastPosition.TOP_CENTER);
      }
    });
  }

  updateOrderStatus() {
    if (!this.orderUUID || !this.order)
      return;
    this.isUpdateStatus = true;
    let nextStatus = '';

    if (this.order.status === 'placed')
      nextStatus = 'preparing_order';
    else if (this.order.status === 'preparing_order')
      nextStatus = 'ready_for_pickup';
    else {
      this.isUpdateStatus = false;
      return;
    }

    this.apiService.updateOrderStatus(this.orderUUID, nextStatus).subscribe({
      next: response => {
        if (this.order)
          this.order.status = response.status;
          this.isUpdateStatus = false;
      },
      error: error => {
        this.isUpdateStatus = false;
        this.toast.createNotification(this.apiErrorService.getDisplayMessage(error), ToastNotificationType.ERROR, '', ToastPosition.TOP_CENTER);
      }
    });
  }

  rejectOrder() {
    if (!this.orderUUID || !this.order || this.order.status !== 'placed')
      return;
    if (!confirm(this.translate.instant('orders.details.actions.reject_confirm')))
      return;

    this.isRejecting = true;
    this.apiService.updateOrderStatus(this.orderUUID, 'rejected').subscribe({
      next: response => {
        if (this.order)
          this.order.status = response.status;
        this.isRejecting = false;
      },
      error: error => {
        // Rejection auto-refunds the order — REFUND_NOT_ELIGIBLE / REFUND_GATEWAY_ERROR
        // mean the status flip was rolled back server-side.
        this.isRejecting = false;
        this.toast.createNotification(this.apiErrorService.getDisplayMessage(error), ToastNotificationType.ERROR, '', ToastPosition.TOP_CENTER);
      }
    });
  }

}
